import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {
    numberedKey,
    TemplateAnalysis,
    TemplateListVariable,
    TemplateNumberedVariable,
    TemplateValueType,
    TemplateValueVariable
} from '../template-variables';
import {TranslateService} from '@ngx-translate/core';

export type TestData = Record<string, any>;

interface TableColumn {
    label: string;
    type: TemplateValueType;
}

/** Un tableau (`rawData.passengers`) ou un groupe de clés numérotées (`passenger_1_email`…), édités en lignes. */
interface TableModel {
    variable: TemplateListVariable | TemplateNumberedVariable;
    columns: TableColumn[];
    /**
     * Lignes, remplacées seulement quand leur nombre change (pour ne pas perdre le focus). Des objets et pas des
     * indices : `p-table` n'affiche pas une ligne dont la valeur est fausse, donc pas la ligne `0`.
     */
    rows: { index: number }[];
}

/**
 * Données de test d'un template : l'objet `rawData` que la landing page recevrait du script Groovy. Le
 * formulaire propose un champ par clé détectée dans le template ; le mode JSON édite l'objet entier, pour
 * ce que la détection ne voit pas.
 */
@Component({
    selector: 'dmp-lp-test-data',
    templateUrl: './test-data.component.html',
    styleUrls: ['./test-data.component.scss'],
    standalone: false
})
export class TestDataComponent implements OnChanges {
    @Input() analysis: TemplateAnalysis;
    @Input() data: TestData = {};
    @Output() dataChange = new EventEmitter<TestData>();

    readonly modes = [
        {label: this.translate.instant('landingPage.template.edit.testData.formMode'), value: 'form'},
        {label: 'JSON', value: 'json'}
    ];
    readonly jsonEditorOptions = {
        theme: 'vs',
        language: 'json',
        lineNumbers: 'on',
        minimap: {enabled: false},
        scrollBeyondLastLine: false,
        automaticLayout: true
    };

    mode: 'form' | 'json' = 'form';
    values: TemplateValueVariable[] = [];
    tables: TableModel[] = [];
    /** Clés présentes dans les données mais que le template ne lit plus (ou pas de façon détectable). */
    extraKeys: string[] = [];
    json = '';
    jsonError: string = null;

    constructor(private readonly translate: TranslateService) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.analysis) {
            const variables = this.analysis?.variables ?? [];
            this.values = variables.filter((v): v is TemplateValueVariable => v.kind === 'value');
            this.tables = variables
                .filter((v): v is TemplateListVariable | TemplateNumberedVariable => v.kind !== 'value')
                .map(variable => ({variable, columns: this.columns(variable), rows: []}));
        }
        if (changes.analysis || changes.data) {
            this.tables.forEach(table => {
                const count = this.rowCount(table.variable);
                if (count !== table.rows.length) {
                    table.rows = Array.from({length: count}, (_, index) => ({index}));
                }
            });
            this.extraKeys = Object.keys(this.data ?? {}).filter(key => !this.isDetected(key));
        }
    }

    get isEmpty(): boolean {
        return !this.values.length && !this.tables.length;
    }

    trackByKey(_: number, item: { key: string } | TableModel): string {
        return 'variable' in item ? item.variable.key : item.key;
    }

    inputType(type: TemplateValueType): string {
        return type === 'text' ? 'text' : type;
    }

    /** Titre d'un tableau : sa clé, ou le motif des clés numérotées. */
    tableHint(table: TableModel): string {
        if (table.variable.kind === 'list') {
            return this.translate.instant('landingPage.template.edit.testData.objectTableHint');
        }
        const variable = table.variable;
        return this.translate.instant('landingPage.template.edit.testData.numberedTableHint',
            {start: variable.start, example: numberedKey(variable.fields[0], variable.start)});
    }

    /* ----- Valeurs simples ----- */

    setValue(variable: TemplateValueVariable, value: unknown): void {
        const next = {...this.data};
        // Un champ vidé retire la clé : la page la reçoit absente, comme si le serveur ne l'envoyait pas.
        if (value === '' || value === null || value === undefined) {
            delete next[variable.key];
        } else {
            next[variable.key] = typed(value, variable.type);
        }
        this.emit(next);
    }

    /* ----- Tableaux et clés numérotées ----- */

    cell(table: TableModel, row: number, column: number): unknown {
        const variable = table.variable;
        if (variable.kind === 'list') {
            return this.data?.[variable.key]?.[row]?.[variable.fields[column]];
        }
        return this.data?.[numberedKey(variable.fields[column], variable.start + row)];
    }

    setCell(table: TableModel, row: number, column: number, value: unknown): void {
        const variable = table.variable;
        const next = {...this.data};
        const cell = value === null || value === undefined ? '' : typed(value, table.columns[column].type);
        if (variable.kind === 'list') {
            const items = [...(next[variable.key] ?? [])];
            items[row] = {...items[row], [variable.fields[column]]: cell};
            next[variable.key] = items;
        } else {
            next[numberedKey(variable.fields[column], variable.start + row)] = cell;
        }
        this.emit(next);
    }

    addRow(table: TableModel): void {
        const variable = table.variable;
        const next = {...this.data};
        if (variable.kind === 'list') {
            const item = variable.fields.reduce((acc, field) => ({...acc, [field]: ''}), {});
            next[variable.key] = [...(Array.isArray(next[variable.key]) ? next[variable.key] : []), item];
        } else {
            // Des clés vides, mais présentes : c'est leur présence qui fait exister la ligne.
            const n = variable.start + table.rows.length;
            variable.fields.forEach(field => next[numberedKey(field, n)] = '');
        }
        this.emit(next);
    }

    removeRow(table: TableModel, row: number): void {
        const variable = table.variable;
        const next = {...this.data};
        if (variable.kind === 'list') {
            next[variable.key] = (next[variable.key] ?? []).filter((_: unknown, i: number) => i !== row);
        } else {
            // Les lignes suivantes remontent d'un numéro, comme dans un tableau.
            const last = table.rows.length - 1;
            for (let i = row; i <= last; i++) {
                variable.fields.forEach(field => {
                    const target = numberedKey(field, variable.start + i);
                    const source = numberedKey(field, variable.start + i + 1);
                    if (i < last && source in next) {
                        next[target] = next[source];
                    } else {
                        delete next[target];
                    }
                });
            }
        }
        this.emit(next);
    }

    /* ----- Actions sur l'ensemble ----- */

    /** Donne une valeur plausible à chaque clé vide, sans toucher à ce qui est déjà saisi. */
    fillExamples(): void {
        const next = {...this.data};
        this.values.forEach(variable => {
            if (next[variable.key] === undefined || next[variable.key] === '') {
                next[variable.key] = sampleValue(variable.key, variable.type, variable.suggestions);
            }
        });
        this.tables.forEach(table => {
            const variable = table.variable;
            const rows = Math.max(table.rows.length, 2);
            if (variable.kind === 'list') {
                const items = Array.isArray(next[variable.key]) ? [...next[variable.key]] : [];
                for (let i = 0; i < rows; i++) {
                    const item = {...items[i]};
                    variable.fields.forEach(field => item[field] = item[field] || sampleValue(field, 'text', [], i));
                    items[i] = item;
                }
                next[variable.key] = items;
            } else {
                for (let i = 0; i < rows; i++) {
                    variable.fields.forEach(field => {
                        const key = numberedKey(field, variable.start + i);
                        next[key] = next[key] || sampleValue(field.label, field.type, [], i);
                    });
                }
            }
        });
        this.emit(next);
    }

    clear(): void {
        this.emit({});
    }

    removeExtraKeys(): void {
        const next = {...this.data};
        this.extraKeys.forEach(key => delete next[key]);
        this.emit(next);
    }

    /* ----- Mode JSON ----- */

    onModeChange(): void {
        if (this.mode === 'json') {
            this.json = JSON.stringify(this.data ?? {}, null, 2);
            this.jsonError = null;
        }
    }

    onJsonChange(json: string): void {
        this.json = json;
        let parsed: unknown;
        try {
            parsed = JSON.parse(json || '{}');
        } catch (error) {
            this.jsonError = this.translate.instant('landingPage.template.edit.testData.jsonInvalidError', {message: (error as Error).message});
            return;
        }
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            this.jsonError = this.translate.instant('landingPage.template.edit.testData.jsonMustBeObjectError');
            return;
        }
        this.jsonError = null;
        this.emit(parsed as TestData);
    }

    private emit(next: TestData): void {
        this.data = next;
        this.dataChange.emit(next);
    }

    private columns(variable: TemplateListVariable | TemplateNumberedVariable): TableColumn[] {
        return variable.kind === 'list'
            ? variable.fields.map(field => ({label: field, type: 'text' as TemplateValueType}))
            : variable.fields.map(field => ({label: field.label, type: field.type}));
    }

    private rowCount(variable: TemplateListVariable | TemplateNumberedVariable): number {
        if (variable.kind === 'list') {
            const items = this.data?.[variable.key];
            return Array.isArray(items) ? items.length : 0;
        }
        let count = 0;
        for (let i = 0; i < 100; i++) {
            if (variable.fields.some(field => numberedKey(field, variable.start + i) in (this.data ?? {}))) {
                count = i + 1;
            }
        }
        return count;
    }

    private isDetected(key: string): boolean {
        return (this.analysis?.variables ?? []).some(variable => {
            if (variable.kind !== 'numbered') {
                return variable.key === key;
            }
            return variable.fields.some(field => key.startsWith(field.prefix) && key.endsWith(field.suffix)
                && /^\d+$/.test(key.slice(field.prefix.length, key.length - field.suffix.length)));
        });
    }
}

/**
 * Valeur saisie au type attendu par le template. Le `type` des saisies est lié (`[type]`) : Angular ne pose
 * alors pas son convertisseur numérique (il exige un `type="number"` écrit en dur), et un nombre arrive en
 * texte. Or `[].constructor('3')` donne un tableau d'un seul élément, là où `[].constructor(3)` en donne trois.
 */
function typed(value: unknown, type: TemplateValueType): unknown {
    if (type === 'number' && typeof value === 'string' && value.trim() !== '' && !isNaN(Number(value))) {
        return Number(value);
    }
    return value;
}

const SAMPLES: [RegExp, string[]][] = [
    [/mail/i, ['marie.durand@example.com', 'paul.martin@example.com']],
    [/first.?name|pr[ée]nom/i, ['Marie', 'Paul']],
    [/last.?name|(^|_)nom($|_)/i, ['Durand', 'Martin']],
    [/civilit/i, ['Mrs', 'Mr']],
    [/phone|mobile|t[ée]l/i, ['+33612345678', '+33698765432']],
    [/zip|postal/i, ['69001', '75008']],
    [/city|ville/i, ['Lyon', 'Paris']],
    [/country|pays/i, ['FR', 'BE']],
    [/address|adresse/i, ['12 rue de la République', '3 avenue Foch']],
    [/airport|a[ée]roport|iata/i, ['LYS', 'CDG']],
    [/lang/i, ['FR', 'EN']],
    [/(^|_)id($|_)|uuid/i, ['1001', '1002']]
];

/** Valeur d'exemple d'après le nom de la clé ; la `index`-ième pour une ligne de tableau. */
function sampleValue(key: string, type: TemplateValueType, suggestions: string[], index = 0): string | number {
    if (suggestions.length) {
        return suggestions[0];
    }
    if (type === 'number') {
        return 2;
    }
    if (type === 'date') {
        // Une date de naissance est dans le passé ; une autre date (départ, commande) plutôt à venir.
        return /birth|naiss/i.test(key)
            ? ['1985-06-15', '1990-03-08'][index % 2]
            : ['2026-11-15', '2026-12-20'][index % 2];
    }
    const sample = SAMPLES.find(([pattern]) => pattern.test(key));
    return sample ? sample[1][index % sample[1].length] : `Exemple ${key}${index ? ' ' + (index + 1) : ''}`;
}
